export interface MentionMember {
  publicId: string
  firebaseUserId?: string
  name: string
  profilePicture?: string
}

// Stored canonical mention token: @[Display Name](mention:<user_public_id>).
// Mirrors the backend parsing regex; group 1 = display name, group 2 = uuid.
const MENTION_TOKEN_REGEX = /@\[([^\]]*)\]\(mention:([0-9a-fA-F-]{36})\)/g

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Editor text -> stored canonical markdown. The editor represents a mention by the
// member's display name (@Jane Doe); we resolve it to a known member and replace it
// with the canonical @[Name](mention:uuid) token. Members are matched longest-name
// first so "@Jane Doe" wins over a shorter member "@Jane", and a match must end on a
// word boundary so "@Jan" never matches inside "@Janet". Unrecognised @text is left
// as plain text. Email is never used here, so it never needs to be surfaced.
export function editorTextToMarkdown(text: string, members: MentionMember[]): string {
  const byLongestName = [...members].sort((a: MentionMember, b: MentionMember): number => b.name.length - a.name.length)

  let result = text
  byLongestName.forEach((member: MentionMember): void => {
    if (
      !member.name ||
      members.filter((candidate) => candidate.name.toLowerCase() === member.name.toLowerCase()).length > 1
    ) {
      return
    }
    // (start-or-whitespace) @Name (followed by end-or-non-word), case-insensitive.
    const pattern = new RegExp(`(^|\\s)@${escapeRegExp(member.name)}(?=$|[^\\w])`, 'gi')
    result = result.replace(pattern, (_match: string, prefix: string): string => {
      return `${prefix}@[${member.name}](mention:${member.publicId})`
    })
  })
  return result
}

// Stored canonical markdown -> editor text, for populating the textarea when editing.
// The display name lives in the token itself, so this just unwraps it to @Name; no
// member lookup (or email) is needed.
export function markdownToEditorText(markdown: string): string {
  return markdown.replace(MENTION_TOKEN_REGEX, (_match: string, name: string): string => {
    return `@${name}`
  })
}

// Stored canonical markdown -> display markdown: @[Name](mention:uuid) -> [@Name](mention:uuid).
// The stored token keeps the @ outside the link (mirroring the backend regex), but for
// rendering we want the @ inside the link text so it sits within the mention chip.
export function mentionMarkdownToDisplayMarkdown(markdown: string): string {
  return markdown.replace(MENTION_TOKEN_REGEX, (_match: string, name: string, uuid: string): string => {
    return `[@${name}](mention:${uuid})`
  })
}

// Stored canonical markdown -> plaintext mirror: @[Name](mention:uuid) -> @Name.
// Other markdown is left as-is. Used for the comment_text plaintext field.
export function mentionMarkdownToPlainText(markdown: string): string {
  return markdown.replace(MENTION_TOKEN_REGEX, (_match: string, name: string): string => {
    return `@${name}`
  })
}

// Extract the deduped, in-order set of mentioned member public_ids from stored markdown.
export function extractMentionPublicIds(markdown: string): string[] {
  const ids: string[] = []
  const seen = new Set<string>()
  for (const match of markdown.matchAll(MENTION_TOKEN_REGEX)) {
    const uuid = match[2]
    if (!seen.has(uuid)) {
      seen.add(uuid)
      ids.push(uuid)
    }
  }
  return ids
}

// Keep canonical identities outside an edited display range. Editing inside a
// mention turns that mention into ordinary text; unchanged mentions retain IDs.
export function replaceMentionEditorRange(markdown: string, start: number, end: number, replacement: string): string {
  let displayOffset = 0
  let sourceOffset = 0
  const tokens: { start: number; end: number; raw: string }[] = []
  for (const match of markdown.matchAll(MENTION_TOKEN_REGEX)) {
    const matchIndex = match.index ?? 0
    displayOffset += matchIndex - sourceOffset
    const length = match[1].length + 1
    tokens.push({ start: displayOffset, end: displayOffset + length, raw: match[0] })
    displayOffset += length
    sourceOffset = matchIndex + match[0].length
  }
  const display = markdownToEditorText(markdown)
  const preserve = (from: number, to: number): string => {
    let result = ''
    let cursor = from
    for (const token of tokens) {
      if (token.start >= from && token.end <= to) {
        result += display.slice(cursor, token.start) + token.raw
        cursor = token.end
      }
    }
    return result + display.slice(cursor, to)
  }
  return preserve(0, start) + replacement + preserve(end, display.length)
}

export function updateMentionEditorText(markdown: string, text: string): string {
  const previous = markdownToEditorText(markdown)
  let start = 0
  while (start < previous.length && start < text.length && previous[start] === text[start]) start++
  let oldEnd = previous.length
  let newEnd = text.length
  while (oldEnd > start && newEnd > start && previous[oldEnd - 1] === text[newEnd - 1]) {
    oldEnd--
    newEnd--
  }
  return replaceMentionEditorRange(markdown, start, oldEnd, text.slice(start, newEnd))
}
